import { create } from 'zustand';
import type { ProbeGeschlecht, ProbePersonType } from './api';

/**
 * Probecover Stage 1 — probe form store (1:1 port of the booth
 * `familyForm` state from feat/booth-local:src/booth/public/booth.js).
 *
 * In-memory only (no persist). The /figuren form is session-scoped;
 * leaving the page discards the state.
 *
 * Cap enforced inline in `addCard` (`PROBE_FORM_MAX_CARDS = 9`,
 * mirrors backend `PROBE_MAX_PERSONS`).
 *
 * Shape mirrors a `.person` fieldset in the booth DOM:
 *   personType       → fs.dataset.type
 *   ageYears         → `[name=ageYears]` (person) or `[name=animalAge]`
 *                      (tier) — one unified `ageYears` state field for
 *                      the probe (both branches read/write the same).
 *   geschlecht       → `.person__fields--person .toggle__btn[aria-pressed]`
 *   species          → `.species__text` (free-text) / `.species__btn`
 *                      (Hund / Katze shortcuts) — one unified `species`
 *                      state field (both buttons write it + a free-text
 *                      edit clears the button selection).
 *   photoFile        → `[name=photo]` file (held in memory until Submit;
 *                      the booth also keeps the raw File blob and uploads
 *                      at submit time rather than per-pick, so the picker
 *                      can be re-opened without an upload race).
 *   photoPreviewUrl  → booth loads a `/api/photo-preview` dataURL thumb;
 *                      the probe uses `URL.createObjectURL(file)` locally
 *                      because the PWA path runs the real sanitizer
 *                      server-side at step_sanitize (see the
 *                      orchestrator's chain). HEIC previews via sharp
 *                      inside the browser are a Stage 2 refinement.
 *   photoKey         → populated by the batch signed-upload at submit
 *                      time (same wire as the book flow's upload-plan).
 */

export const PROBE_FORM_MAX_CARDS = 9;

export interface ProbeFormCard {
  readonly id: string;
  personType: ProbePersonType;
  ageYears: number | null;
  geschlecht: ProbeGeschlecht | '';
  species: string;
  photoFile: File | null;
  photoPreviewUrl: string | null;
  photoKey: string | null;
}

interface ProbeFormState {
  readonly cards: readonly ProbeFormCard[];
  addCard: () => void;
  removeCard: (id: string) => void;
  updateCard: (id: string, patch: Partial<ProbeFormCard>) => void;
  setPhoto: (id: string, file: File | null) => void;
  setPhotoKey: (id: string, key: string | null) => void;
  reset: () => void;
}

function makeCard(): ProbeFormCard {
  return {
    id:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `card_${Math.random().toString(36).slice(2, 10)}`,
    personType: 'person',
    ageYears: null,
    geschlecht: '',
    species: '',
    photoFile: null,
    photoPreviewUrl: null,
    photoKey: null,
  };
}

function revokePreview(url: string | null): void {
  if (url === null) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* non-fatal: shared view may already be revoked */
  }
}

export const useProbeStore = create<ProbeFormState>((set) => ({
  cards: [makeCard()],
  addCard: () =>
    set((state) => {
      if (state.cards.length >= PROBE_FORM_MAX_CARDS) return state;
      return { cards: [...state.cards, makeCard()] };
    }),
  removeCard: (id) =>
    set((state) => {
      if (state.cards.length <= 1) return state;
      const target = state.cards.find((c) => c.id === id);
      revokePreview(target?.photoPreviewUrl ?? null);
      return { cards: state.cards.filter((c) => c.id !== id) };
    }),
  updateCard: (id, patch) =>
    set((state) => ({
      cards: state.cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    })),
  setPhoto: (id, file) =>
    set((state) => ({
      cards: state.cards.map((c) => {
        if (c.id !== id) return c;
        revokePreview(c.photoPreviewUrl);
        if (file === null) {
          return { ...c, photoFile: null, photoPreviewUrl: null, photoKey: null };
        }
        let previewUrl: string | null = null;
        try {
          previewUrl = URL.createObjectURL(file);
        } catch {
          previewUrl = null;
        }
        // Reset the upload key — the next submit must re-upload. Mirrors
        // the booth's `delete photoWrap.dataset.previewOk` on re-pick.
        return { ...c, photoFile: file, photoPreviewUrl: previewUrl, photoKey: null };
      }),
    })),
  setPhotoKey: (id, key) =>
    set((state) => ({
      cards: state.cards.map((c) => (c.id === id ? { ...c, photoKey: key } : c)),
    })),
  reset: () =>
    set((state) => {
      for (const c of state.cards) revokePreview(c.photoPreviewUrl);
      return { cards: [makeCard()] };
    }),
}));
